import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { combineLatest, Subscription } from 'rxjs';

interface RecommendedJob {
  title: string;
  company: string;
  country: string;
  score: number;
  matchedKeywords: string[];
}

@Component({
  selector: 'app-atw',
  templateUrl: './atw.component.html',
  styleUrl: './atw.component.scss'
})
export class AtwComponent implements OnInit, AfterViewInit, OnDestroy {
  form = {
    name: '',
    phone: '',
    email: ''
  };

  selectedFile: File | null = null;
  dragOver = false;
  currentLanguage = 'en';
  isUploading = false;
  progress = 0;

  errorMessage = '';
  successMessage = '';

  keywords: string[] = [];
  jobs: RecommendedJob[] = [];

  heroLoading = true;

  typedTitleLine1 = '';
  typedTitleStrong = '';
  typedTitleLine2 = '';
  fullHeroTitle = '';
  typewriterDone = false;

  heroVideoSrc = '/assets/videos/atw-hero-1.mp4';

  private readonly heroVideoStorageKey = 'atw-selected-hero-video';
  private readonly fallbackHeroVideoPath = '/assets/videos/atw-hero-1.mp4';

  private readonly heroVideoPaths: string[] = [
    this.fallbackHeroVideoPath,
    '/assets/videos/atw-hero-2.mp4',
    '/assets/videos/atw-hero-3.mp4',
    '/assets/videos/atw-hero-4.mp4',
    '/assets/videos/atw-hero-5.mp4'
  ];

  private heroVideoReady = false;
  private heroTextReady = false;
  private heroTypewriterStarted = false;

  private heroLine1 = '';
  private heroStrong = '';
  private heroLine2 = '';

  private readonly maxFileSize = 5 * 1024 * 1024;
  private readonly typingSpeed = 55;
  private readonly typingPause = 180;
  private readonly restartTypingDelay = 1800;

  private typingIntervals: number[] = [];
  private typingTimeouts: number[] = [];

  private langSub?: Subscription;
  private heroTextSub?: Subscription;
  private heroFallbackTimeout?: number;

  private readonly isBrowser: boolean;

  constructor(
    private translocoService: TranslocoService,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      this.currentLanguage = localStorage.getItem('language') || 'en';
    }
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.setRandomHeroVideo();
    this.setupHeroTextLoader();

    this.langSub = this.translocoService.langChanges$.subscribe((lang) => {
      this.currentLanguage = lang || localStorage.getItem('language') || 'en';
    });

    this.heroFallbackTimeout = window.setTimeout(() => {
      this.heroVideoReady = true;
      this.tryFinishHeroLoading();
    }, 2500);
  }

  ngAfterViewInit(): void {
    this.muteAllHomeVideos();
  }

  ngOnDestroy(): void {
    this.clearTypingTimers();

    if (this.heroFallbackTimeout) {
      window.clearTimeout(this.heroFallbackTimeout);
    }

    this.langSub?.unsubscribe();
    this.heroTextSub?.unsubscribe();
  }

  private setRandomHeroVideo(): void {
    const previousVideo = sessionStorage.getItem(this.heroVideoStorageKey);
    const nextVideo = this.getRandomHeroVideo(previousVideo);

    this.heroVideoSrc = nextVideo;
    sessionStorage.setItem(this.heroVideoStorageKey, nextVideo);

    this.heroVideoReady = false;
    this.heroLoading = true;

    window.setTimeout(() => {
      this.muteAllHomeVideos();
    });
  }

  private getRandomHeroVideo(previousVideo?: string | null): string {
    if (!this.heroVideoPaths.length) {
      return this.fallbackHeroVideoPath;
    }

    if (this.heroVideoPaths.length === 1) {
      return this.heroVideoPaths[0];
    }

    const availableVideos = this.heroVideoPaths.filter(
      (video) => video !== previousVideo
    );

    const randomIndex = Math.floor(Math.random() * availableVideos.length);

    return availableVideos[randomIndex];
  }

  private setupHeroTextLoader(): void {
    this.heroTextSub = combineLatest([
      this.translocoService.selectTranslate('atw.hero.titleLine1'),
      this.translocoService.selectTranslate('atw.hero.titleStrong'),
      this.translocoService.selectTranslate('atw.hero.titleLine2')
    ]).subscribe(([line1, strong, line2]) => {
      if (!line1 || !strong || !line2) {
        return;
      }

      this.clearTypingTimers();

      this.heroLine1 = line1;
      this.heroStrong = strong;
      this.heroLine2 = line2;

      this.fullHeroTitle = `${line1} ${strong} ${line2}`;

      this.typedTitleLine1 = '';
      this.typedTitleStrong = '';
      this.typedTitleLine2 = '';
      this.typewriterDone = false;

      this.heroTextReady = true;
      this.heroTypewriterStarted = false;

      this.tryFinishHeroLoading();
    });
  }

  onHeroVideoReady(event?: Event): void {
    if (event) {
      this.muteVideo(event);
      this.playVideo(event);
    }

    if (!this.isBrowser || this.heroVideoReady) {
      return;
    }

    this.heroVideoReady = true;
    this.tryFinishHeroLoading();
  }

  onHeroVideoError(event?: Event): void {
    if (this.heroVideoSrc !== this.fallbackHeroVideoPath) {
      this.heroVideoSrc = this.fallbackHeroVideoPath;
      sessionStorage.setItem(
        this.heroVideoStorageKey,
        this.fallbackHeroVideoPath
      );

      window.setTimeout(() => {
        this.reloadHeroVideo();
        this.muteAllHomeVideos();
      });
    }

    this.onHeroVideoReady(event);
  }

  muteVideo(event: Event): void {
    const video = event.target as HTMLVideoElement | null;

    if (!video) {
      return;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
  }

  private playVideo(event: Event): void {
    const video = event.target as HTMLVideoElement | null;

    if (!video) {
      return;
    }

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {
        this.heroVideoReady = true;
        this.tryFinishHeroLoading();
      });
    }
  }

  private reloadHeroVideo(): void {
    if (!this.isBrowser) {
      return;
    }

    const video = this.host.nativeElement.querySelector(
      '.hero-background-video'
    ) as HTMLVideoElement | null;

    if (!video) {
      return;
    }

    video.load();
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => undefined);
    }
  }

  private muteAllHomeVideos(): void {
    if (!this.isBrowser) {
      return;
    }

    this.host.nativeElement.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
    });
  }

  private tryFinishHeroLoading(): void {
    if (!this.heroVideoReady || !this.heroTextReady) {
      return;
    }

    this.heroLoading = false;

    if (!this.heroTypewriterStarted) {
      this.heroTypewriterStarted = true;
      this.startHeroTypewriter();
    }
  }

  private startHeroTypewriter(): void {
    if (!this.isBrowser) {
      return;
    }

    this.clearTypingTimers();

    this.typedTitleLine1 = '';
    this.typedTitleStrong = '';
    this.typedTitleLine2 = '';
    this.typewriterDone = false;

    const line1 = this.heroLine1;
    const strong = this.heroStrong;
    const line2 = this.heroLine2;
    const line2WithSpace = ` ${line2}`;

    this.fullHeroTitle = `${line1} ${strong} ${line2}`;

    this.typeText(
      line1,
      (value) => {
        this.typedTitleLine1 = value;
      },
      () => {
        this.delayTyping(() => {
          this.typeText(
            strong,
            (value) => {
              this.typedTitleStrong = value;
            },
            () => {
              this.delayTyping(() => {
                this.typeText(
                  line2WithSpace,
                  (value) => {
                    this.typedTitleLine2 = value;
                  },
                  () => {
                    this.typewriterDone = true;

                    this.delayTyping(() => {
                      this.restartHeroTypewriter();
                    }, this.restartTypingDelay);
                  }
                );
              });
            }
          );
        });
      }
    );
  }

  private typeText(
    text: string,
    update: (value: string) => void,
    done?: () => void
  ): void {
    let index = 0;

    const interval = window.setInterval(() => {
      update(text.slice(0, index + 1));
      index++;

      if (index >= text.length) {
        window.clearInterval(interval);
        done?.();
      }
    }, this.typingSpeed);

    this.typingIntervals.push(interval);
  }

  private delayTyping(
    callback: () => void,
    delay: number = this.typingPause
  ): void {
    const timeout = window.setTimeout(() => {
      callback();
    }, delay);

    this.typingTimeouts.push(timeout);
  }

  private restartHeroTypewriter(): void {
    this.typedTitleLine1 = '';
    this.typedTitleStrong = '';
    this.typedTitleLine2 = '';
    this.typewriterDone = false;

    this.delayTyping(() => {
      this.startHeroTypewriter();
    }, 300);
  }

  private clearTypingTimers(): void {
    this.typingIntervals.forEach((interval) => {
      window.clearInterval(interval);
    });

    this.typingTimeouts.forEach((timeout) => {
      window.clearTimeout(timeout);
    });

    this.typingIntervals = [];
    this.typingTimeouts = [];
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.setFile(file);
    }

    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.setFile(file);
    }
  }

  setFile(file: File): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.keywords = [];
    this.jobs = [];

    const isPdf =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      this.selectedFile = null;
      this.errorMessage = 'Please upload a PDF file only.';
      return;
    }

    if (file.size > this.maxFileSize) {
      this.selectedFile = null;
      this.errorMessage = 'PDF size must be less than 5MB.';
      return;
    }

    this.selectedFile = file;
  }

  removeFile(event: MouseEvent): void {
    event.stopPropagation();

    this.selectedFile = null;
    this.progress = 0;
    this.isUploading = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.keywords = [];
    this.jobs = [];
  }

  submitCv(event: Event): void {
    event.preventDefault();

    this.errorMessage = '';
    this.successMessage = '';

    if (!this.selectedFile) {
      this.errorMessage = 'Please upload your CV first.';
      return;
    }

    if (!this.form.name.trim()) {
      this.errorMessage = 'Please enter your full name.';
      return;
    }

    if (!this.form.phone.trim()) {
      this.errorMessage = 'Please enter your phone number.';
      return;
    }

    this.isUploading = true;
    this.progress = 0;
    this.keywords = [];
    this.jobs = [];

    const interval = window.setInterval(() => {
      this.progress += 10;

      if (this.progress >= 100) {
        window.clearInterval(interval);

        this.isUploading = false;
        this.successMessage =
          'CV analyzed successfully. Matching jobs are ready.';

        this.keywords = [
          'Dentist',
          'Saudi Arabia',
          'Master Degree',
          'English',
          'Healthcare',
          'Experience'
        ];

        this.jobs = [
          {
            title: 'Dentist Specialist',
            company: 'Verified Medical Center',
            country: 'Saudi Arabia',
            score: 96,
            matchedKeywords: ['Dentist', 'Saudi Arabia', 'Experience']
          },
          {
            title: 'Dental Consultant',
            company: 'International Clinic Group',
            country: 'Riyadh',
            score: 91,
            matchedKeywords: ['Healthcare', 'English', 'Master Degree']
          },
          {
            title: 'General Dentist',
            company: 'Premium Dental Company',
            country: 'Jeddah',
            score: 87,
            matchedKeywords: ['Dentist', 'Experience']
          }
        ];
      }
    }, 180);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  scrollToUploader(): void {
    if (!this.isBrowser) {
      return;
    }

    document.getElementById('cvUploader')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}
